import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";

const TERMINAL_STATUS_FILTERS: PropertyFilterProps.Token[] = [
  { propertyKey: "status", operator: "!=", value: "Complete" },
  { propertyKey: "status", operator: "!=", value: "Cancelled" },
];

export const STAT_TO_TAB_MAP: Record<string, string> = {
  myOpenTasks: "assigned-to-me",
  myTasksOverdue: "assigned-to-me",
  myTasksDueIn7Days: "assigned-to-me",
  myTasksDueIn30Days: "assigned-to-me",
  myAgentNeedsInput: "assigned-to-me",
  myTeamTasksOverdue: "my-team",
  myTeamTasksDueIn7Days: "my-team",
  myTeamTasksDueIn30Days: "my-team",
  myTeamOpenTasks: "my-team",
  myTeamGuestOwnedOpenTasks: "my-team",
  myTeamOpenTasksAssignedToInactiveMembers: "my-team",
  teamMemberAsGuestOpenTasks: "guest-assignments",
  teamMemberAsGuestOpenTasksOverdue: "guest-assignments",
  teamMemberAsGuestOpenTasksDueIn7Days: "guest-assignments",
  teamMemberAsGuestOpenTasksDueIn30Days: "guest-assignments",
  teamQueueAll: "team-queue",
  teamQueueOverdue: "team-queue",
  teamQueueDueIn7Days: "team-queue",
  teamQueueDueIn30Days: "team-queue",
};

export interface StatCardDef {
  key: string;
  label: string;
  statKey: string;
  color?: string;
  infoContent?: string;
}

export const ASSIGNED_TO_ME_STATS: StatCardDef[] = [
  {
    key: "myTasksOverdue",
    label: "Overdue",
    statKey: "myTasksOverdueCount",
    color: "text-status-error",
  },
  {
    key: "myTasksDueIn7Days",
    label: "Due in 7 days",
    statKey: "myTasksDueIn7DaysCount",
  },
  {
    key: "myTasksDueIn30Days",
    label: "Due in 30 days",
    statKey: "myTasksDueIn30DaysCount",
  },
  { key: "myOpenTasks", label: "All open tasks", statKey: "myOpenTasksCount" },
];

export const AGENT_NEEDS_INPUT_STAT: StatCardDef = {
  key: "myAgentNeedsInput",
  label: "⚠️ Agent needs input",
  statKey: "myAgentNeedsInputCount",
  infoContent: "Tasks where the agent is awaiting your input to continue.",
};

export const MY_TEAM_STATS: StatCardDef[] = [
  {
    key: "myTeamTasksOverdue",
    label: "Overdue",
    statKey: "myTeamTasksOverdueCount",
    color: "text-status-error",
  },
  {
    key: "myTeamTasksDueIn7Days",
    label: "Due in 7 days",
    statKey: "myTeamTasksDueIn7DaysCount",
  },
  {
    key: "myTeamTasksDueIn30Days",
    label: "Due in 30 days",
    statKey: "myTeamTasksDueIn30DaysCount",
  },
  {
    key: "myTeamOpenTasks",
    label: "All open tasks",
    statKey: "myTeamOpenTasksCount",
  },
  {
    key: "myTeamGuestOwnedOpenTasks",
    label: "Guest ownership",
    statKey: "myTeamGuestOwnedOpenTasksCount",
    infoContent:
      "Tasks owned by your team but assigned to guests from other teams.",
  },
  {
    key: "myTeamOpenTasksAssignedToInactiveMembers",
    label: "⚠️ Needs reassignment",
    statKey: "myTeamOpenTasksAssignedToInactiveMembersCount",
    infoContent:
      "Tasks assigned to former team members who are no longer part of the team.",
  },
];

export const GUEST_ASSIGNMENT_STATS: StatCardDef[] = [
  {
    key: "teamMemberAsGuestOpenTasks",
    label: "Tasks in other teams",
    statKey: "teamMemberAsGuestOpenTasksCount",
  },
  {
    key: "teamMemberAsGuestOpenTasksOverdue",
    label: "Overdue",
    statKey: "teamMemberAsGuestOpenTasksOverdueCount",
    color: "text-status-error",
  },
  {
    key: "teamMemberAsGuestOpenTasksDueIn7Days",
    label: "Due in 7 days",
    statKey: "teamMemberAsGuestOpenTasksDueIn7DaysCount",
  },
  {
    key: "teamMemberAsGuestOpenTasksDueIn30Days",
    label: "Due in 30 days",
    statKey: "teamMemberAsGuestOpenTasksDueIn30DaysCount",
  },
];

export const TEAM_QUEUE_STATS: StatCardDef[] = [
  {
    key: "teamQueueOverdue",
    label: "Overdue",
    statKey: "teamQueueOverdueCount",
    color: "text-status-error",
  },
  {
    key: "teamQueueDueIn7Days",
    label: "Due in 7 days",
    statKey: "teamQueueDueIn7DaysCount",
  },
  {
    key: "teamQueueDueIn30Days",
    label: "Due in 30 days",
    statKey: "teamQueueDueIn30DaysCount",
  },
  {
    key: "teamQueueAll",
    label: "All unassigned",
    statKey: "teamQueueAllCount",
  },
];

/**
 * Generates PropertyFilter query configurations for task stat cards.
 * Translates backend filter logic from DynamoDBTaskDaoHelper into UI filter queries.
 *
 * @param statType - The type of stat card clicked
 * @param currentUser - The current user's identifier for "my tasks" filters
 * @returns PropertyFilterProps.Query object with appropriate filter tokens
 */
export const getFilterForStat = (
  statType: string,
  currentUser?: string,
  teamName?: string,
  guests?: string[],
  inactiveMembers?: string[],
): PropertyFilterProps.Query => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of today
  const todayStr = today.toISOString(); // Full ISO 8601 format for OpenSearch
  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(today.getDate() + 7);
  sevenDaysFromNow.setHours(23, 59, 59, 999); // End of day
  const sevenDaysStr = sevenDaysFromNow.toISOString();
  const thirtyDaysFromNow = new Date(today);
  thirtyDaysFromNow.setDate(today.getDate() + 30);
  thirtyDaysFromNow.setHours(23, 59, 59, 999); // End of day
  const thirtyDaysStr = thirtyDaysFromNow.toISOString();

  switch (statType) {
    // Assigned to me: (owners = me OR assigneeOwner = me) AND status != Complete AND status != Cancelled
    case "myOpenTasks":
      return {
        tokens: [],
        tokenGroups: [
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "owners",
                operator: "=",
                value: currentUser || "",
              },
              {
                propertyKey: "assigneeOwner",
                operator: "=",
                value: currentUser || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myAgentNeedsInput":
      return {
        tokens: [],
        tokenGroups: [
          {
            propertyKey: "agentStatus",
            operator: "=",
            value: "Awaiting input",
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "owners",
                operator: "=",
                value: currentUser || "",
              },
              {
                propertyKey: "assigneeOwner",
                operator: "=",
                value: currentUser || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myTasksOverdue":
      return {
        tokens: [],
        tokenGroups: [
          {
            propertyKey: "dueDate",
            operator: "<",
            value: todayStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "owners",
                operator: "=",
                value: currentUser || "",
              },
              {
                propertyKey: "assigneeOwner",
                operator: "=",
                value: currentUser || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myTasksDueIn30Days":
      return {
        tokens: [],
        tokenGroups: [
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: thirtyDaysStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "owners",
                operator: "=",
                value: currentUser || "",
              },
              {
                propertyKey: "assigneeOwner",
                operator: "=",
                value: currentUser || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myTasksDueIn7Days":
      return {
        tokens: [],
        tokenGroups: [
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: sevenDaysStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "owners",
                operator: "=",
                value: currentUser || "",
              },
              {
                propertyKey: "assigneeOwner",
                operator: "=",
                value: currentUser || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    // my team: (teamName = currentTeam OR assigneeTeam = currentTeam) AND status != Complete AND status != Cancelled
    case "myTeamTasksOverdue":
      return {
        tokens: [],
        tokenGroups: [
          {
            propertyKey: "dueDate",
            operator: "<",
            value: todayStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "teamName",
                operator: "=",
                value: teamName || "",
              },
              {
                propertyKey: "assigneeTeam",
                operator: "=",
                value: teamName || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myTeamTasksDueIn30Days":
      return {
        tokens: [],
        tokenGroups: [
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: thirtyDaysStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "teamName",
                operator: "=",
                value: teamName || "",
              },
              {
                propertyKey: "assigneeTeam",
                operator: "=",
                value: teamName || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myTeamTasksDueIn7Days":
      return {
        tokens: [],
        tokenGroups: [
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: sevenDaysStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "teamName",
                operator: "=",
                value: teamName || "",
              },
              {
                propertyKey: "assigneeTeam",
                operator: "=",
                value: teamName || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myTeamOpenTasks":
      return {
        tokens: [],
        tokenGroups: [
          ...TERMINAL_STATUS_FILTERS,
          {
            operation: "or",
            tokens: [
              {
                propertyKey: "teamName",
                operator: "=",
                value: teamName || "",
              },
              {
                propertyKey: "assigneeTeam",
                operator: "=",
                value: teamName || "",
              },
            ],
          },
        ],
        operation: "and",
      } as any;

    case "myTeamGuestOwnedOpenTasks":
      return {
        tokens: [
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "teamName",
            operator: "=",
            value: teamName || "",
          },
          // Use "=" operator with array value - PropertyFilter will show as multiple tokens
          // transformPropertyFilterQuery will convert this to "in" operator for backend
          {
            propertyKey: "owners",
            operator: "=",
            value: guests as any, // Array value with "=" operator
          },
        ],
        operation: "and",
      };

    case "myTeamOpenTasksAssignedToInactiveMembers":
      return {
        tokens: [
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "teamName",
            operator: "=",
            value: teamName || "",
          },
          // Use "=" operator with array value
          {
            propertyKey: "owners",
            operator: "=",
            value: inactiveMembers as any, // Array value with "=" operator
          },
        ],
        operation: "and",
      };

    // Guest assignments: teamName != currentTeam AND assigneeTeam != currentTeam AND status != Complete AND status != Cancelled
    case "teamMemberAsGuestOpenTasks":
      return {
        tokens: [
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "teamName",
            operator: "!=",
            value: teamName,
          },
          {
            propertyKey: "assigneeTeam",
            operator: "!=",
            value: teamName,
          },
        ],
        operation: "and",
      };

    case "teamMemberAsGuestOpenTasksOverdue":
      return {
        tokens: [
          {
            propertyKey: "dueDate",
            operator: "<",
            value: todayStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "teamName",
            operator: "!=",
            value: teamName,
          },
          {
            propertyKey: "assigneeTeam",
            operator: "!=",
            value: teamName,
          },
        ],
        operation: "and",
      };

    case "teamMemberAsGuestOpenTasksDueIn30Days":
      return {
        tokens: [
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: thirtyDaysStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "teamName",
            operator: "!=",
            value: teamName,
          },
          {
            propertyKey: "assigneeTeam",
            operator: "!=",
            value: teamName,
          },
        ],
        operation: "and",
      };

    case "teamMemberAsGuestOpenTasksDueIn7Days":
      return {
        tokens: [
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: sevenDaysStr,
          },
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "teamName",
            operator: "!=",
            value: teamName,
          },
          {
            propertyKey: "assigneeTeam",
            operator: "!=",
            value: teamName,
          },
        ],
        operation: "and",
      };

    // Team queue
    // Team queue: assigneeTeam=currentTeam AND assigneeOwner IS NULL/empty enforced by TEAM_QUEUE scope.
    case "teamQueueAll":
      return {
        tokens: [...TERMINAL_STATUS_FILTERS],
        operation: "and",
      };

    case "teamQueueOverdue":
      return {
        tokens: [
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "dueDate",
            operator: "<",
            value: todayStr,
          },
        ],
        operation: "and",
      };

    case "teamQueueDueIn7Days":
      return {
        tokens: [
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: sevenDaysStr,
          },
        ],
        operation: "and",
      };

    case "teamQueueDueIn30Days":
      return {
        tokens: [
          ...TERMINAL_STATUS_FILTERS,
          {
            propertyKey: "dueDate",
            operator: ">=",
            value: todayStr,
          },
          {
            propertyKey: "dueDate",
            operator: "<=",
            value: thirtyDaysStr,
          },
        ],
        operation: "and",
      };

    default:
      return {
        tokens: [],
        operation: "and",
      };
  }
};
